import React, { useState } from 'react';
import { CallTreeNode } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Box, ArrowRight, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

interface CallTreeProps {
  root: CallTreeNode;
}

interface NodeProps {
  node: CallTreeNode;
  depth?: number;
}

const Node: React.FC<NodeProps> = ({ node, depth = 0 }) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'CALL': return 'text-blue-400';
      case 'STATICCALL': return 'text-purple-400';
      case 'DELEGATECALL': return 'text-yellow-400';
      case 'CREATE':
      case 'CREATE2': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="flex flex-col">
      <div 
        className={cn(
          "group flex items-center gap-2 py-1.5 px-2 hover:bg-white/5 cursor-pointer transition-colors border-l border-transparent hover:border-emerald-500/30",
          node.error && "bg-red-500/5 hover:bg-red-500/10"
        )}
        onClick={() => setIsOpen(!isOpen)}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <div className="flex items-center gap-1 w-4">
          {hasChildren && (
            isOpen ? <ChevronDown className="w-3 h-3 text-[#525252]" /> : <ChevronRight className="w-3 h-3 text-[#525252]" />
          )}
        </div>
        
        <span className={cn("font-mono text-[10px] font-bold w-20", getTypeColor(node.type))}>
          {node.type}
        </span>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="font-mono text-[10px] text-[#A3A3A3] truncate">
            {node.from.slice(0, 6)}...{node.from.slice(-4)}
          </span>
          <ArrowRight className="w-3 h-3 text-[#404040] shrink-0" />
          <span className="font-mono text-[10px] text-white truncate">
            {node.to.slice(0, 6)}...{node.to.slice(-4)}
          </span>
        </div>

        {node.error && (
          <div className="flex items-center gap-1 text-red-400 ml-auto">
            <AlertCircle className="w-3 h-3" />
            <span className="font-mono text-[9px] uppercase tracking-tighter">{node.error}</span>
          </div>
        )}

        <div className="hidden group-hover:flex items-center gap-3 ml-auto text-[9px] font-mono text-[#525252]">
          <span>GAS: {node.gasUsed.toLocaleString()}</span>
          {node.value && node.value !== '0' && (
            <span className="text-emerald-500/70">VAL: {node.value}</span>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            {node.children!.map((child, i) => (
              <Node key={`${child.id}-${i}`} node={child} depth={depth + 1} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export function CallTree({ root }: CallTreeProps) {
  return (
    <div className="w-full h-full bg-[#0A0A0A] flex flex-col overflow-hidden">
      <div className="h-10 border-bottom border-[#262626] flex items-center px-4 justify-between bg-[#0F0F0F]">
        <div className="flex items-center gap-2">
          <Box className="w-4 h-4 text-emerald-500" />
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-white">Call Hierarchy</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-[9px] text-emerald-500 font-mono">
            SUCCESS
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto custom-scrollbar py-2">
        <Node node={root} />
      </div>
    </div>
  );
}
